import { Link } from "react-router-dom";
import { ArrowLeft, Zap } from "lucide-react";
import { BRAND, DISCORD_URL } from "@/lib/constants";
import { useSeo } from "@/lib/seo";

const CONTACT_EMAIL = "management@flowtweaks.com";

const Section = ({ title, children }) => (
  <section className="mb-12">
    <h2
      className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-5"
      style={{ fontFamily: "'Orbitron', sans-serif" }}
    >
      <span className="text-orange-500 mr-3">/</span>
      {title}
    </h2>
    <div className="text-zinc-300 leading-relaxed text-base sm:text-[17px] space-y-4">
      {children}
    </div>
  </section>
);

const Mail = () => (
  <a
    href={`mailto:${CONTACT_EMAIL}`}
    className="text-orange-500 hover:text-orange-400 underline underline-offset-4 decoration-orange-500/40"
  >
    {CONTACT_EMAIL}
  </a>
);

const Discord = ({ children }) => (
  <a
    href={DISCORD_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="text-orange-500 hover:text-orange-400 underline underline-offset-4 decoration-orange-500/40"
  >
    {children}
  </a>
);

export default function Privacy() {
  useSeo({
    title: "Privacy Policy — Flow Tweaks",
    description:
      "What Flow Tweaks collects, why, who we share it with, and how to get a copy of your data or have it deleted.",
    path: "/privacy",
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="privacy-page">
      <header className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />
            <span
              className="font-black tracking-[0.18em] text-sm uppercase"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              {BRAND.name}
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <p className="text-orange-500 font-bold tracking-[0.3em] text-xs uppercase mb-4">Legal</p>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mb-4 leading-[0.95]"
          style={{ fontFamily: "'Orbitron', sans-serif" }}
        >
          Privacy Policy
        </h1>
        <p className="text-zinc-500 text-sm uppercase tracking-[0.2em] mb-16">
          Last updated · {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </p>

        <p className="text-zinc-400 text-sm leading-relaxed mb-12 border border-orange-500/20 bg-orange-500/[0.03] p-5">
          Short version: we collect your email so we can deliver what you bought and support you
          afterwards, we never see your card details, we do not sell your data to anyone, and you
          can have all of it deleted by emailing <Mail />.
        </p>

        <Section title="1. Who We Are">
          <p>
            Flow Tweaks is a sole proprietorship operating under the laws of the State of Missouri,
            United States. We are the data controller for the information described on this page.
          </p>
          <p>
            Contact: <Mail /> — or open a ticket in our <Discord>Discord server</Discord>. Email is
            the right channel for anything about your data.
          </p>
        </Section>

        <Section title="2. What We Collect">
          <p>
            <span className="text-white font-bold">Account and order data.</span> Your email address
            — we use it to log you in, confirm your purchase, unlock your plan, and contact you
            about your order. Your name and Discord username if you give them to us. The plan you
            bought, the amount charged, and a Stripe transaction reference.
          </p>
          <p>
            <span className="text-white font-bold">Support content.</span> The messages and files
            you send us through Discord tickets or support chat, including any diagnostic report
            you choose to share.
          </p>
          <p>
            <span className="text-white font-bold">Technical data on orders.</span> Order
            submissions log the IP address and browser user-agent behind them, for fraud prevention
            and troubleshooting.
          </p>
          <p>
            <span className="text-white font-bold">Referral program data.</span> If you sign up as a
            referrer we store your email, your Discord handle, your referral code, and the balances
            and reward history attached to it.
          </p>
          <p>
            <span className="text-white font-bold">Analytics data.</span> See section 5 — this is
            the only category that depends on your consent.
          </p>
        </Section>

        <Section title="3. Payments">
          <p>
            We never see or store your card details. Payment is entered directly into the hosted
            checkout page run by Stripe. We receive only the plan you bought, the amount charged,
            and a Stripe transaction reference. Stripe processes that payment as an independent
            controller under its own privacy policy.
          </p>
        </Section>

        <Section title="4. The Desktop App">
          <p>
            The diagnostic report and support code produced by the desktop app are generated and
            saved locally on your own PC. We do not receive them automatically. You decide if and
            when to share one with us, for example to get help with a support ticket.
          </p>
          <p>
            The app signs in to our backend with the email attached to your purchase so it knows
            which packages to unlock. That is the only thing it sends us on its own.
          </p>
        </Section>

        <Section title="5. Cookies and Analytics">
          <p>
            <span className="text-white font-bold">Always on — no cookies, no consent needed.</span>{" "}
            Vercel Analytics and Vercel Speed Insights give us aggregate page-view counts and page
            load timings. They are cookieless, they do not set an identifier, and they cannot be
            used to single you out.
          </p>
          <p>
            <span className="text-white font-bold">Consent-gated.</span> Two tools load only if you
            allow them:
          </p>
          <ul className="list-disc pl-5 space-y-2 marker:text-orange-500">
            <li>
              <span className="text-white font-bold">PostHog</span> — product analytics and{" "}
              <span className="text-white">session recording</span>. Session recording captures how
              you move through and interact with our pages, so we can see where the site confuses
              people. It sets cookies and an identifier for your browser.
            </li>
            <li>
              <span className="text-white font-bold">X (Twitter) advertising pixel</span> — reports
              whether a visit that came from an X ad ended in a purchase, so we can tell whether our
              ads are worth running.
            </li>
          </ul>
          <p>
            If you are visiting from the EU, EEA or UK, neither of those loads until you press
            Accept on the cookie banner. Press Decline and we never load them at all — nothing on
            the site stops working. Outside those regions they load by default; email <Mail /> if
            you would like us to exclude you and we will.
          </p>
          <p>
            Your choice is stored in your own browser, in local storage. Clearing your browser data
            clears the choice and we will ask again.
          </p>
        </Section>

        <Section title="6. Why We Use It">
          <p>
            To deliver what you paid for, unlock your plan, answer your support requests, run the
            referral program, prevent fraud and chargeback abuse, meet our tax and accounting
            obligations, and — where you have allowed it — understand how the site is used and
            whether our advertising works.
          </p>
        </Section>

        <Section title="7. Who We Share It With">
          <p>
            We do not sell your personal information to anyone. We share it only with the service
            providers that make the product run:
          </p>
          <ul className="list-disc pl-5 space-y-2 marker:text-orange-500">
            <li><span className="text-white">Stripe</span> — payments and payouts</li>
            <li><span className="text-white">Vercel</span> — website hosting and cookieless analytics</li>
            <li><span className="text-white">Render</span> — backend hosting</li>
            <li><span className="text-white">MongoDB Atlas</span> — database</li>
            <li><span className="text-white">PostHog</span> — product analytics, only with consent</li>
            <li><span className="text-white">X (Twitter)</span> — advertising measurement, only with consent</li>
            <li><span className="text-white">Discord</span> — support and delivery, under its own policy</li>
          </ul>
          <p>
            We may also disclose information where we are legally required to, or where it is
            necessary to investigate fraud or abuse of our services.
          </p>
        </Section>

        <Section title="8. How Long We Keep It">
          <p>
            Order and payment records are kept for as long as we need them for accounting and tax
            purposes. Account, referral and support data is kept while your account is active and
            for a reasonable period afterwards, in case you come back or dispute a charge. Analytics
            data is retained under the defaults of the provider that collected it.
          </p>
        </Section>

        <Section title="9. Your Rights">
          <p>
            You can ask us for a copy of the data we hold about you, ask us to correct it, or ask us
            to delete it. Email <Mail /> from the address attached to your order and we will handle
            it. There is no charge.
          </p>
          <p>
            Deleting your data means losing access to any plan unlocked against that email, so we
            will confirm with you before we do it.
          </p>
          <p>
            If you are in the EU, EEA or UK you also have the right to object to processing, to
            restrict it, to data portability, and to complain to your local data protection
            authority.
          </p>
        </Section>

        <Section title="10. Children">
          <p>
            Our services are not directed at children under 13 and we do not knowingly collect their
            information. If you believe a child has given us personal information, email <Mail /> and
            we will delete it.
          </p>
        </Section>

        <Section title="11. Changes">
          <p>
            We may update this policy as the product changes. The date at the top of this page
            always reflects the current version. Material changes to what we collect, or to who we
            share it with, will be announced in our Discord.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            Flow Tweaks — a sole proprietorship, Missouri, United States.
          </p>
          <p>
            Email: <Mail />
          </p>
          <p>
            Support: <Discord>our Discord server</Discord>
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-white/5">
          <Link
            to="/terms"
            className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors"
          >
            Read the Terms of Service →
          </Link>
        </div>
      </main>
    </div>
  );
}
